import { axiosInstance, useApi } from '@fides/fides-react-components';
import { AxiosError, AxiosResponse } from 'axios';
import { TableParams } from 'core/interfaces/TableParams';
import { endpoints } from '../consts';
import { DebitParams, VersionInformation } from '../interfaces';
import { Currency } from '../models';

export const getEFT = (restUrl: string, params: TableParams | DebitParams): Promise<AxiosResponse> =>
  axiosInstance.get(restUrl, { baseURL: endpoints.EFTApiUrl, params });

/** Header used to tell the backend which IANA timezone the browser is in, so date defaults
 * (e.g. the payment execution date) can be computed against the user's own calendar day. */
export const CLIENT_TIMEZONE_HEADER = 'X-Client-Timezone';

/** Returns the header object with the browser's own IANA timezone (e.g. "Asia/Bangkok"), to be
 * spread into an Axios request config's `headers`. Falls back to an empty object if the
 * timezone cannot be determined, so requests are unaffected. */
export const getClientTimezoneHeader = (): Record<string, string> => {
  try {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    return timeZone ? { [CLIENT_TIMEZONE_HEADER]: timeZone } : {};
  } catch {
    return {};
  }
};

const CACHE_TTL_MS = 5 * 1000;

interface CacheEntry {
  promise: Promise<AxiosResponse>;
  expireAt: number;
}

const eftCache = new Map<string, CacheEntry>();

export const getEFTDeduped = (restUrl: string, params: TableParams | DebitParams): Promise<AxiosResponse> => {
  const key = `${restUrl}::${JSON.stringify(params)}`;
  const cached = eftCache.get(key);

  if (cached !== undefined && Date.now() < cached.expireAt) {
    return cached.promise;
  }

  eftCache.delete(key);

  const promise = axiosInstance.get(restUrl, { baseURL: endpoints.EFTApiUrl, params }).catch((error: AxiosError) => {
    eftCache.delete(key);
    throw error;
  });

  eftCache.set(key, { promise, expireAt: Date.now() + CACHE_TTL_MS });
  return promise;
};

export const getCurrencies = (): Promise<Currency[]> =>
  axiosInstance
    .get(`${endpoints.ARSApiUrl}/${endpoints.currencies}`)
    .then((response: AxiosResponse<Currency[]>) => response.data)
    .catch((error: AxiosError) => {
      throw error;
    });

export const useFetchVersionInfo = (): VersionInformation => {
  const [response] = useApi<VersionInformation>({
    baseURL: endpoints.PortalApiUrl,
    url: endpoints.versionInformation,
  });
  return response.data;
};
